import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEOHead from "@/components/SEOHead";

export default function About() {
  return (
    <>
      <SEOHead
        title="About HighestEfficiencySolarCell.com | Independent Solar Panel Buyer's Guide"
        description="Learn about our independent solar panel buyer's guide — our editorial process, how we evaluate panels, affiliate disclosure, and our commitment to accurate information."
        canonical="https://highestefficiencysolarcell.com/about"
      />
    <div className="pb-28 lg:pb-0">
      <div className="max-w-3xl mx-auto px-4 py-12 lg:py-20">
        <nav className="text-sm font-mono text-muted-foreground mb-6">
          <Link to="/" className="hover:text-accent">Home</Link>
          <span className="mx-2">/</span>
          <span>About</span>
        </nav>

        <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-6">About This Site</h1>

        <div className="space-y-6 text-muted-foreground leading-relaxed">
          <p className="text-lg">
            <strong className="text-foreground">HighestEfficiencySolarCell.com</strong> is an independent buyer's
            guide dedicated to helping people understand solar cell and solar panel efficiency, and choose the
            right solar products for real-world use. We publish detailed buyer's guides, product comparisons,
            sizing calculators, and educational articles for consumers navigating the rapidly evolving solar panel market.
          </p>
          <p>
            We believe that the best solar panel isn't always the one with the highest efficiency number on the
            spec sheet. It's the panel that delivers the best performance for your specific setup — whether that's
            an RV roof, a camping trip, a backyard cabin, or a full off-grid installation. A 24% efficient IBC panel
            is overkill for a weekend camper; a 100W foldable panel is exactly right.
          </p>
          <p>
            Our guides focus on practical buyer questions: How much solar do I actually need? Which panel type
            fits my space and budget? What's the difference between PERC, HJT, TOPCon, and IBC cells? We break down
            the engineering jargon into plain language and help you make informed decisions without wading through
            marketing copy.
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground pt-4">What We Cover</h2>
          <p>
            Our content library spans the full spectrum of consumer solar topics:
          </p>
          <ul className="space-y-2 list-disc list-inside">
            <li><strong className="text-foreground">Solar panel technology</strong> — PERC, HJT, TOPCon, IBC, bifacial, flexible, and standard rigid panels explained in plain language</li>
            <li><strong className="text-foreground">Use-case buyer's guides</strong> — specific recommendations for RV owners, campers, van lifers, cabin builders, and off-grid homeowners</li>
            <li><strong className="text-foreground">Compatibility guides</strong> — which panels work with Jackery, EcoFlow, Bluetti, and other popular portable power stations</li>
            <li><strong className="text-foreground">Sizing calculators and guides</strong> — how to figure out exactly how many watts of solar you need for any application</li>
            <li><strong className="text-foreground">Technical how-tos</strong> — wiring panels in series vs parallel, choosing charge controllers, connecting panels to power stations</li>
          </ul>

          <h2 id="editorial-team" className="font-heading font-bold text-2xl text-foreground pt-4 scroll-mt-24">Who Is Responsible for This Site?</h2>
          <p>
            Content is published under the <strong className="text-foreground">Highest Efficiency Solar Cell Editorial Team</strong>.
            That byline identifies the organization responsible for researching, maintaining, correcting, and updating the material on this site.
            We do not invent individual expert identities or credentials. When a guide is specification-based rather than hands-on tested, we say so directly.
          </p>
          <p>
            Reader corrections are welcome. If a manufacturer specification, compatibility detail, warranty term, or technical statement changes,
            contact us and we will review the source and update the page when warranted.
          </p>

          <h2 id="editorial-process" className="font-heading font-bold text-2xl text-foreground pt-4 scroll-mt-24">Our Editorial Process</h2>
          <p>
            Our guides are built from manufacturer specification sheets, warranty documents, public technical
            references, independent research where available, and buyer feedback used as a secondary signal. We
            separate published specifications from anecdotal reports and do not present buyer comments as laboratory
            evidence.
          </p>
          <p>
            We update our guides regularly as new panels enter the market, prices change, and technology
            evolves. The solar panel market moves fast — what was the best panel in 2023 may not be the best
            in 2026. We revise our recommendations accordingly.
          </p>
          <p>
            Manufacturer claims are identified as claims or specifications rather than presented as our own test
            results. We do not reprint press releases as reviews or accept payment to move a product higher in our
            rankings. Our criteria include efficiency, form-factor fit, compatibility, warranty quality, documented
            specifications, and recurring buyer-reported issues.
          </p>

          <h3 className="font-heading font-bold text-xl text-foreground pt-2">Primary Sources We Prefer</h3>
          <p>
            Technical explanations are checked against primary or near-primary references whenever practical. Core references include the
            U.S. Department of Energy's photovoltaic technology, cell-efficiency, and system-design resources; NREL's research-cell efficiency
            records; and current manufacturer manuals or datasheets for model-specific voltage, current, connector, warranty, and installation details.
            Individual guides now display a visible source panel so readers can inspect the references themselves.
          </p>

          <h3 className="font-heading font-bold text-xl text-foreground pt-2">AI-Assisted Content Policy</h3>
          <p>
            Automation and generative AI may assist with topic research, outlining, first-draft language, metadata, or identifying questions readers are asking.
            We do not treat automated generation as evidence. Technical claims must be supportable by published specifications or technical references,
            and product ratings, prices, test results, review counts, or availability are never invented.
          </p>
          <p>
            We previously experimented with a higher-frequency automated publishing workflow. That workflow has been paused. We now consolidate overlapping
            search topics into stronger guides rather than publishing multiple pages that answer substantially the same question. New indexable pages must add
            a distinct decision framework, calculation, source set, compatibility method, or other useful information beyond a keyword variation.
          </p>

          <h3 className="font-heading font-bold text-xl text-foreground pt-2">How We Use Affiliate Links</h3>
          <p>
            Amazon links are purchase links, not evidence. We do not copy Amazon star ratings into our structured data and do not use Amazon review counts
            as proof of product quality. Product cards are shown only when we have a direct product URL or ASIN, and current price and availability are left
            to the retailer because those details can change quickly.
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground pt-4">How We Make Money</h2>
          <p>
            This site is supported through the Amazon Associates affiliate program. When you click a product link
            and make a purchase on Amazon, we earn a small commission at no extra cost to you. This is how we fund
            the time required to research, write, and maintain accurate buyer's guides.
          </p>
          <p>
            Affiliate links can create a financial incentive when a reader buys after clicking. We account for that
            conflict by disclosing affiliate links, avoiding paid placement in rankings, and evaluating products with
            the same published criteria regardless of price.
          </p>
          <p>
            We never hardcode prices. Solar panel prices on Amazon change frequently — sometimes daily. Every
            product page has a "Check Price on Amazon" link that shows you the current, live price. We do not
            display prices we cannot keep accurate.
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground pt-4">How to Read Our Recommendations</h2>
          <p>
            Unless a page explicitly documents hands-on testing, our recommendations are editorial evaluations based
            on published information and product-market research. We distinguish between manufacturer specifications,
            independent technical references, and buyer-reported experience. When a fact can change — such as price,
            availability, model revision, or warranty wording — readers should verify the current information with the
            manufacturer or retailer before purchasing.
          </p>

          <h2 className="font-heading font-bold text-2xl text-foreground pt-4">Our Standards</h2>
          <ul className="space-y-2 list-disc list-inside">
            <li>We do not fabricate reviews or personal testing claims we have not conducted</li>
            <li>We do not use fake star ratings or made-up performance numbers</li>
            <li>We clearly disclose our affiliate relationship on every product page</li>
            <li>We focus on factual, verifiable specifications from manufacturer data and independent research</li>
            <li>We acknowledge the limitations and trade-offs of every product we recommend</li>
            <li>We do not hide negative reviews or suppress unflattering product information</li>
            <li>We update content when products are discontinued, superseded, or found to have quality issues</li>
          </ul>

          <h2 className="font-heading font-bold text-2xl text-foreground pt-4">Contact Us</h2>
          <p>
            Have a question about solar panels, a correction to one of our guides, or a product you'd like us
            to cover? We welcome reader questions and feedback.
          </p>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-foreground font-mono font-bold text-sm rounded-lg hover:bg-accent/90 min-h-[48px]"
          >
            Contact Us <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/solar-guides"
            className="inline-flex items-center gap-2 px-6 py-3 border border-border text-sm font-medium rounded-lg hover:bg-secondary min-h-[48px]"
          >
            Browse Solar Guides
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}